import { useEffect, useState } from 'react'
import { api, type GaesteBild, type GaesteTag } from '../api'
import { anzahl, datumKurz, euro, euroKurz } from '../format'
import { Anteile } from '../komponenten/Anteile'
import { Balken } from '../komponenten/Balken'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'
import { MehrfachLinie } from '../komponenten/MehrfachLinie'

const ART_NAME: Record<string, string> = {
  SALE: 'Verkauf', SPLIT: 'Teilrechnung', TRANSFER: 'Umbuchung',
  VOID: 'Storno', RECALL: 'Wiedereröffnung',
}

export function Gaeste({ von, bis, betriebe }: { von: string; bis: string; betriebe: string[] }) {
  const [d, setD] = useState<GaesteBild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.gaeste(von, bis, betriebe)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  if (!d.vorhanden) {
    return (
      <div className="karte"><div className="leer" style={{ maxWidth: '62ch', margin: '0 auto', lineHeight: 1.7 }}>
        <b style={{ color: 'var(--schrift)' }}>Für diesen Zeitraum liegt kein Quittungsreport vor.</b>
        <div style={{ marginTop: 10 }}>
          Gästezahl, Tisch und Tischdauer stehen nicht im Kassenarchiv,
          sondern nur in diesem zweiten Bericht. Ohne ihn bleibt diese
          Ansicht leer — und das ist richtiger, als Zahlen zu zeigen, die
          es nicht gibt.
        </div>
      </div></div>
    )
  }

  // Über den ganzen Zeitraum, aus Summen gebildet — nicht aus Tagesquoten
  // gemittelt: sonst zählte ein schwacher Montag so viel wie ein starker
  // Samstag.
  //
  // Und jede Quote mit genau dem Nenner, aus dem ihr Zähler stammt. Wer
  // „Umsatz je Gast" aus Gesamtumsatz durch Gästezahl bildet, bekommt
  // 48,20 statt 47,83 — im Zähler stecken dann Belege, die gar keine
  // Gästezahl tragen. Dieselbe Falle bei „Gäste je Tisch": über alle
  // Belege gerechnet 2,53, über Verkaufsbelege 2,24.
  const tage: GaesteTag[] = d.tage
  const summe = (f: (t: GaesteTag) => number) =>
    tage.reduce((s, t) => s + f(t), 0)

  const gaeste = summe((t) => t.gaeste)
  const gaesteBereinigt = summe((t) => t.gaeste_bereinigt)
  const tischbesuche = summe((t) => t.tischbesuche)
  const tischbesucheBereinigt = summe((t) => t.tischbesuche_bereinigt)
  const umsatzMitGaesten = summe((t) => Number(t.umsatz_mit_gaesten))
  const gaestePositiv = summe((t) => t.gaeste_positiv)
  const gaesteAufVerkaeufen = summe((t) => t.gaeste_auf_verkaeufen)
  const verkaeufeMitGaesten = summe((t) => t.verkaeufe_mit_gaesten)
  const belegeMitTisch = summe((t) => t.belege_mit_tisch)
  const belegeFuerTisch = summe((t) => t.belege_fuer_tisch)
  const mehrereTage = d.tage.length > 1
  // Tisch 621 gibt es in jedem Standort. Steht mehr als einer im Bild,
  // gehoert der Name dazu - sonst liest sich die Liste, als haette ein
  // Tisch das Vierzehnfache umgesetzt.
  const mehrereStandorte = new Set(d.tische.map((t) => t.betrieb_id)).size > 1

  return (
    <>
      <div className="gitter g-4" style={{ marginBottom: 14 }}>
        <Kennzahl etikett="Gäste · roh" wert={anzahl(gaeste)}
                  fuss={mehrereTage ? `über ${d.tage.length} Tage` : undefined} />
        <Kennzahl etikett="Gäste · bereinigt" wert={anzahl(gaesteBereinigt)}
                  fuss="ohne unbezahlte Personal- und Bruchbelege" />
        <Kennzahl etikett="Tischbelegungen · roh" wert={anzahl(tischbesuche)} />
        <Kennzahl etikett="Tischbelegungen · bereinigt"
                  wert={anzahl(tischbesucheBereinigt)}
                  fuss="bezahlte gemischte Tische bleiben enthalten" />
      </div>
      <div className="gitter g-4" style={{ marginBottom: 14 }}>
        <Kennzahl etikett="Umsatz je Gast"
                  wert={gaestePositiv
                    ? euro(umsatzMitGaesten / gaestePositiv) : '–'}
                  fuss="nur Belege mit Gästezahl" />
        <Kennzahl etikett="Gäste je Tisch"
                  wert={verkaeufeMitGaesten
                    ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })
                        .format(gaesteAufVerkaeufen / verkaeufeMitGaesten) : '–'}
                  fuss={`${anzahl(belegeMitTisch)} von ${anzahl(belegeFuerTisch)} Belegen tragen einen Tisch`} />
        <Kennzahl etikett="Tischdauer"
                  wert={d.tischdauer.median !== null
                    ? `${d.tischdauer.median} min` : '–'}
                  fuss={d.tischdauer.drei_viertel !== null
                    ? `Median — ein Viertel über ${d.tischdauer.drei_viertel} min`
                    : 'Median, nur Verkäufe'} />
      </div>

      {mehrereTage && (
        <Karte titel="Gäste und Umsatz je Gast im Verlauf">
          <MehrfachLinie
            etiketten={d.tage.map((t) => datumKurz(t.tag))}
            reihen={[
              { schluessel: 'gaeste', name: 'Gäste',
                werte: d.tage.map((t) => t.gaeste) },
            ]}
            formatiere={(v) => anzahl(v)}
            formatiereAchse={(v) => anzahl(Math.round(v))}
            abNull />
          <div style={{ marginTop: 18 }}>
            <MehrfachLinie
              etiketten={d.tage.map((t) => datumKurz(t.tag))}
              reihen={[{
                schluessel: 'umsatz_je_gast', name: 'Umsatz je Gast',
                werte: d.tage.map((t) => t.umsatz_je_gast == null
                  ? null : Number(t.umsatz_je_gast)),
              }]}
              formatiere={euro} formatiereAchse={euroKurz} abNull />
          </div>
        </Karte>
      )}

      <div className="gitter g-2-1" style={{ marginTop: mehrereTage ? 14 : 0 }}>
        <Karte titel="Tischgrößen" neben="nur Verkaufsbelege mit Gästezahl">
          {d.tischgroessen.length ? (
            <>
              <Balken
                werte={d.tischgroessen.map((g) => ({
                  schluessel: String(g.gaeste),
                  etikett: String(g.gaeste),
                  wert: g.belege,
                  neben: euro(g.durchschnittsbon),
                }))}
                formatiere={(v) => `${anzahl(v)} Belege`}
                formatiereAchse={(v) => anzahl(Math.round(v))} />
              <table style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Gäste am Tisch</th>
                    <th className="r">Belege</th>
                    <th className="r">Umsatz</th>
                    <th className="r">Ø Rechnung</th>
                    <th className="r">je Gast</th>
                  </tr>
                </thead>
                <tbody>
                  {d.tischgroessen.map((g) => (
                    <tr key={g.gaeste}>
                      <td>{g.gaeste}</td>
                      <td className="r zahl dezent">{anzahl(g.belege)}</td>
                      <td className="r zahl">{euro(g.umsatz)}</td>
                      <td className="r zahl">{euro(g.durchschnittsbon)}</td>
                      <td className="r zahl">
                        {euro(Number(g.durchschnittsbon) / g.gaeste)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <div className="leer">Keine Belege mit Gästezahl.</div>}
        </Karte>

        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <Karte titel="Belegarten">
            <Anteile
              anteile={d.belegarten.map((b) => ({
                name: ART_NAME[b.art] ?? b.art, wert: b.anzahl,
              }))}
              formatiere={(v) => anzahl(v)} />
            <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
              Teilrechnungen, Umbuchungen und Stornos tragen dieselbe
              Öffnungs- wie Abschlusszeit, also die Dauer null. Sie zählen
              deshalb nicht in die Tischdauer — sie würden den Median
              drücken, ohne dass ein Tisch kürzer besetzt gewesen wäre.
            </p>
          </Karte>

          <Karte titel="Quellenabgleich" neben="Archiv gegen Quittungsreport">
            <table>
              <tbody>
                <tr>
                  <td>Quittungen</td>
                  <td className="r zahl">{anzahl(d.quellenabgleich.quittungen)}</td>
                </tr>
                <tr>
                  <td>davon im Archiv wiedergefunden</td>
                  <td className="r zahl">{anzahl(d.quellenabgleich.mit_beleg)}</td>
                </tr>
                <tr>
                  <td>ohne eigenen Archivbeleg</td>
                  <td className="r zahl">{anzahl(d.quellenabgleich.ohne_beleg)}</td>
                </tr>
                <tr>
                  <td>Mehrdeutig, nicht zugeordnet</td>
                  <td className="r zahl">{anzahl(d.quellenabgleich.mehrdeutig)}</td>
                </tr>
              </tbody>
            </table>
            {d.quellenabgleich.mehrdeutig > 0 && (
              <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
                Für diese Quittungen gibt es mehrere Archivbelege mit derselben
                Rechnungskennung am selben Tisch. Keiner wurde zugeordnet.
              </p>
            )}
            {d.quellenabgleich.mit_beleg === 0 && (
              <p className="dezent" style={{ fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6 }}>
                Keine eindeutige Übereinstimmung. Das kann an einer fehlenden
                Quelle oder an Belegarten ohne eigenen Archivbeleg liegen.
              </p>
            )}
          </Karte>
        </div>
      </div>

      <div className="gitter g-2" style={{ marginTop: 14 }}>
        <Karte titel="Bereiche" neben="aus dem Tischnamen">
          {d.bereiche.length ? (
            <table>
              <thead>
                <tr>
                  <th>Bereich</th>
                  <th className="r">Tische</th>
                  <th className="r">Besuche</th>
                  <th className="r">Gäste</th>
                  <th className="r">Umsatz</th>
                  <th className="r">je Gast</th>
                </tr>
              </thead>
              <tbody>
                {d.bereiche.map((b) => (
                  <tr key={b.bereich}>
                    <td>{b.bereich}</td>
                    <td className="r zahl dezent">{anzahl(b.tische)}</td>
                    <td className="r zahl dezent">{anzahl(b.besuche)}</td>
                    <td className="r zahl">{anzahl(b.gaeste)}</td>
                    <td className="r zahl">{euro(b.umsatz)}</td>
                    <td className="r zahl">{euro(b.umsatz_je_gast)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="leer">Keine Bereiche erkannt.</div>}
        </Karte>

        <Karte titel="Tische nach Umsatz" neben="Teilrechnungen zusammengefasst">
          {d.tische.length ? (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Tisch</th>
                    <th className="r">Besuche</th>
                    <th className="r">Umsatz</th>
                    <th className="r">je Besuch</th>
                    <th className="r">Dauer</th>
                  </tr>
                </thead>
                <tbody>
                  {d.tische.map((t) => (
                    <tr key={`${t.betrieb_id}-${t.tisch}`}>
                      <td>
                        {t.tisch}
                        {mehrereStandorte && (
                          <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                            {t.betrieb}
                          </span>
                        )}
                        {t.bereich && (
                          <span className="dezent" style={{ marginLeft: 7, fontSize: 11 }}>
                            {t.bereich}
                          </span>
                        )}
                      </td>
                      <td className="r zahl dezent">{anzahl(t.besuche)}</td>
                      <td className="r zahl">{euro(t.umsatz)}</td>
                      <td className="r zahl">{euro(t.umsatz_je_besuch)}</td>
                      <td className="r zahl dezent">
                        {t.dauer_median === null ? '–' : `${t.dauer_median} min`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="leer">Keine Tische im Zeitraum.</div>}
        </Karte>
      </div>
    </>
  )
}
